import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Badge, Fab, Tooltip } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments } from "@fortawesome/free-solid-svg-icons/faComments";
import { useSiteContent } from "../../SiteContentContext";
import { useLeadChat } from "../../LeadChatContext";
import { useCookieConsent } from "@Components/consent/CookieConsentProvider";
import { recordCta } from "../../marketing";
import { siteTokens } from "../../siteTokens";

// The widget (session, polling, reCAPTCHA, panel UI) only downloads once the
// visitor actually opens the chat — a page view never mints a session.
const LeadChatWidget = lazy(() => import("./LeadChatWidget"));

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  fab: {
    "&&": {
      position: "fixed",
      right: theme.spacing(2.5),
      zIndex: 1250,
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 14px 28px ${tk.accentDeepA(0.36)}`,
      // Lifted above the cookie banner with a transform (--banner-lift),
      // never by changing `bottom`: a `bottom` change when the banner shows
      // up is a layout shift (PageSpeed flagged this button on mobile).
      bottom: "calc(20px + var(--lead-chat-sticky-offset, 0px))",
      transform: "translateY(calc(-1 * var(--banner-lift, 0px)))",
      transition: "transform 200ms ease",
      "&:hover": { transform: "translateY(calc(-1 * var(--banner-lift, 0px) - 2px))" },
    },
  },
  };
});

const readChatParam = (): boolean =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("chat");

const LeadChatLauncher: React.FC = () => {
  const classes = useStyles();
  const { content } = useSiteContent();
  const { isOpen, activated, unread, open } = useLeadChat();
  const { bannerHeight } = useCookieConsent();
  const copy = content.leadChat;
  const deepLinked = useRef(false);

  // /fidava?chat=1 opens the chat on load (same idea as ?demo=1).
  useEffect(() => {
    if (deepLinked.current || !readChatParam()) return;
    deepLinked.current = true;
    open();
  }, [open]);

  // Clear of the cookie banner always (--banner-lift, a transform), and of
  // the mobile sticky CTA bar (≈76px) on small screens.
  const lift = { ["--banner-lift" as any]: `${bannerHeight}px` } as React.CSSProperties;

  return (
    <>
      <style>{`@media (max-width: 899.95px) { :root { --lead-chat-sticky-offset: 76px; } }`}</style>
      {!isOpen && (
        <Tooltip title={copy.launcher} placement="left">
          <Fab
            className={classes.fab}
            style={lift}
            aria-label={copy.launcher}
            onClick={() => {
              recordCta("lead-chat-open", { section: "lead-chat" });
              open();
            }}
          >
            <Badge color="error" variant="dot" invisible={unread === 0} overlap="circular">
              <FontAwesomeIcon icon={faComments} size="lg" />
            </Badge>
          </Fab>
        </Tooltip>
      )}
      {activated && (
        <Suspense fallback={null}>
          <LeadChatWidget />
        </Suspense>
      )}
    </>
  );
};

export default LeadChatLauncher;
